import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { RequestOptions } from '@crossnokaye/typescript-sdk/lib/sdks';

import { gaiaClient as baseGaiaClient } from '@/client/gaia';
import type { GaiaClientFunction } from '@/client/types';
import { FEATURE_FLAGS } from '@/constants';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import {
  DISPLAY_TEMPLATE_SOURCE_HEADER,
  resolveDisplayTemplateSource,
} from '@/queries/gaia-api/display-template-source-key';
import { displayTemplateSourceOverrideAtom } from '@/state/display-template-source.state';

import { useAuth } from './use-auth';

/**
 * Hook that provides an auth-aware Gaia client.
 * This hook uses the auth context to automatically include authentication headers.
 * Also sends X-Display-Template-Source from the ux-config flag (temporary; UX-4500),
 * unless a DevTools session override is set.
 * Stamps displayTemplateSource on the client for React Query key partitioning.
 */
export const useGaiaClient = (): GaiaClientFunction => {
  const { getAccessToken } = useAuth();
  const flags = useFeatureFlags();
  const override = useAtomValue(displayTemplateSourceOverrideAtom);
  const displayTemplateSource = resolveDisplayTemplateSource(
    flags[FEATURE_FLAGS.UX_CONFIG],
    override
  );

  return useMemo(() => {
    const client: GaiaClientFunction = async <TRequest, TResponse>(
      params: Omit<Parameters<typeof baseGaiaClient<TRequest, TResponse>>[0], 'getAccessToken'>
    ) => {
      const headers = new Headers(params.options?.headers);
      headers.set(DISPLAY_TEMPLATE_SOURCE_HEADER, displayTemplateSource);
      const options: RequestOptions = {
        ...params.options,
        headers,
      };
      return baseGaiaClient({
        ...params,
        options,
        getAccessToken,
      });
    };
    client.displayTemplateSource = displayTemplateSource;
    return client;
  }, [getAccessToken, displayTemplateSource]);
};
